import { useFeatureAccess } from "../../../access-control/hooks/useFeatureAccess";
import { FEATURE_KEYS } from "../../../access-control/models/role-feature.models";
import HoldCleaningRunLifecycle from "../../../hold-cleaning-intelligence/components/HoldCleaningRunLifecycle";
import {
  formatHoldCleaningDate,
  formatHoldCleaningSource,
  HoldCleaningMockBanner,
  HoldCleaningSourceStateBadge,
  HoldCleaningStatusBadge,
} from "../../../hold-cleaning-intelligence/components/HoldCleaningUi";
import { HOLD_CLEANING_EMAIL_OUTCOME_LABELS } from "../../../hold-cleaning-intelligence/config/email-outcomes.constants";
import type {
  HoldCleaningCoverageMetric,
  HoldCleaningFunctionalCoverageKey,
  HoldCleaningRefreshRun,
  HoldCleaningRefreshStatus,
  HoldCleaningEmailIntelligenceStatus,
  HoldCleaningSourceHealth,
} from "../../../hold-cleaning-intelligence/models/hold-cleaning.models";
import {
  getHoldCleaningErrorMessage,
  getHoldCleaningRefreshRun,
  getHoldCleaningEmailIntelligenceStatus,
  getHoldCleaningRefreshStatus,
  getHoldCleaningSourceHealth,
  listHoldCleaningRefreshRuns,
} from "../../../hold-cleaning-intelligence/services/hold-cleaning.service";
import { ADMIN_PATHS } from "../../models/admin.models";
import { adminStyles } from "../../styles/admin.styles";
import HoldCleaningRunCost from "./HoldCleaningRunCost";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const duration = (run: HoldCleaningRefreshRun | null): string => {
  if (!run?.startedAt) return "-";
  const end = run.completedAt ? new Date(run.completedAt).getTime() : Date.now();
  return `${Math.max(0, Math.round((end - new Date(run.startedAt).getTime()) / 1_000))}s`;
};

const COVERAGE_FIELD_LABELS: Record<HoldCleaningFunctionalCoverageKey, { label: string; meaning: string }> = {
  CLEANING_PLAN: { label: "Plan or method mentioned", meaning: "Plan report, cleaning window, method or man-hours" },
  CLEANING_STEPS: { label: "Cleaning step mentioned", meaning: "At least one of the 13 steps; not all steps complete" },
  HOLD_PROGRESS: { label: "Hold progress mentioned", meaning: "At least one hold has an explicit progress value" },
  WORK_LOG: { label: "Work entry mentioned", meaning: "At least one extracted work-log entry" },
  OUTSTANDING_ITEMS: {
    label: "Outstanding item mentioned",
    meaning: "At least one item reported, whether open or resolved; silence is unverified",
  },
  INVENTORY: { label: "Inventory item mentioned", meaning: "An item found in email, excluding Veslink form inventory" },
  FAILURES_OR_WARNINGS: {
    label: "Failure or warning mentioned",
    meaning: "At least one reported issue; absence is unverified",
  },
  MAINTENANCE: { label: "Maintenance event mentioned", meaning: "At least one reported event; absence is unverified" },
  CLEANING_STANDARD: { label: "Cleaning standard mentioned", meaning: "An explicit required standard found in email" },
};

const metricTones = {
  success: "bg-[#CCE4E6] text-[#007681]",
  warning: "bg-[#FFF8E1] text-[#946200]",
  danger: "bg-[#FFEAED] text-[#BF0002]",
  neutral: "bg-white dark:bg-gray-800",
};

const HoldCleaningMonitoringPanel = () => {
  const [status, setStatus] = useState<HoldCleaningRefreshStatus | null>(null);
  const [health, setHealth] = useState<HoldCleaningSourceHealth[]>([]);
  const [runs, setRuns] = useState<HoldCleaningRefreshRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<HoldCleaningRefreshRun | null>(null);
  const [emailStatus, setEmailStatus] = useState<HoldCleaningEmailIntelligenceStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { hasAccess: canOpenCrewReport } = useFeatureAccess(FEATURE_KEYS.ADMIN_CREW_REPORT);
  const failureBreakdown = emailStatus?.failureBreakdown ?? [];
  const coverage = emailStatus?.coverage;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextStatus, sourceHealth, runPage, nextEmailStatus] = await Promise.allSettled([
        getHoldCleaningRefreshStatus(),
        getHoldCleaningSourceHealth(),
        listHoldCleaningRefreshRuns(1, 10),
        getHoldCleaningEmailIntelligenceStatus(),
      ]);
      if (nextStatus.status === "fulfilled") setStatus(nextStatus.value);
      if (sourceHealth.status === "fulfilled") setHealth(sourceHealth.value);
      if (runPage.status === "fulfilled") setRuns(runPage.value.items);
      if (nextEmailStatus.status === "fulfilled") setEmailStatus(nextEmailStatus.value);
      const failed = [nextStatus, sourceHealth, runPage, nextEmailStatus].find(
        (result) => result.status === "rejected",
      );
      if (failed?.status === "rejected") setError(getHoldCleaningErrorMessage(failed.reason));
    } catch (loadError) {
      setError(getHoldCleaningErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);

  useEffect(() => {
    if (!status?.activeRun && !emailStatus?.latestRunUsage?.imageCategorizationRunning) return;
    const timer = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(timer);
  }, [load, status?.activeRun, emailStatus?.latestRunUsage?.imageCategorizationRunning]);

  const inspectRun = useCallback(async (runId: string) => {
    try {
      setSelectedRun(await getHoldCleaningRefreshRun(runId));
    } catch (detailError) {
      setError(getHoldCleaningErrorMessage(detailError));
    }
  }, []);

  useEffect(() => {
    if (!selectedRun || selectedRun.completedAt) return;
    const timer = window.setInterval(() => void inspectRun(selectedRun.runId), 10_000);
    return () => window.clearInterval(timer);
  }, [selectedRun, inspectRun]);

  return (
    <main
      className={twMerge("h-full overflow-y-auto", adminStyles.page.pagePadding)}
      data-test="admin-hold-cleaning-monitoring-panel"
    >
      <HoldCleaningMockBanner />
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className={adminStyles.heading.eyebrow}>Monitoring</p>
          <h1 className={twMerge("mt-1", adminStyles.heading.pageTitle)}>Hold Cleaning refresh</h1>
          <p className="mt-2 text-sm opacity-75">
            Daily at {status?.schedule.localTime ?? "05:00"} {status?.schedule.timeZone ?? "Europe/Zurich"}; read-only
            operational state.
          </p>
        </div>
        {!status?.activeRun && (
          <button
            type="button"
            className="inline-flex h-10 items-center gap-2 border px-4 text-sm font-semibold"
            onClick={() => void load()}
          >
            <ArrowPathIcon className={twMerge("h-4 w-4", loading && "animate-spin")} /> Reload
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 border border-[#F59692] bg-[#FFEAED] px-4 py-3 text-sm text-[#BF0002]">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric
          label="Active"
          value={status?.activeRun ? status.activeRun.status : "No"}
          tone={status?.activeRun ? "warning" : "success"}
        />
        <Metric label="Pending" value={status?.pendingCount ?? 0} />
        <Metric label="Last success" value={formatHoldCleaningDate(status?.latestSuccess?.completedAt)} />
        <Metric
          label="Last failure"
          value={formatHoldCleaningDate(status?.latestFailure?.completedAt)}
          tone={status?.latestFailure ? "danger" : "neutral"}
        />
      </div>

      {runs
        .filter((run) => run.runId === status?.activeRun?.runId)
        .map((run) => (
          <HoldCleaningRunLifecycle key={run.runId} run={run} />
        ))}
      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <section className={twMerge(adminStyles.card.surface, "p-5 xl:col-span-2")}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold">What cleaning information do we have?</h2>
              <p className="mt-1 text-xs opacity-70">Counts of voyages with information available to review.</p>
            </div>
            <Link
              className="text-xs font-semibold text-green underline"
              to={`${ADMIN_PATHS.debugCronJobs}?job=hold-cleaning-refresh`}
            >
              Continue backfill in Debug
            </Link>
          </div>
          {coverage && (
            <div className="mt-5 border-t pt-5" data-test="hold-cleaning-coverage-summary">
              <p className="rounded-lg bg-[#F7FBFA] px-4 py-3 text-sm text-[#35504B] dark:bg-gray-900 dark:text-gray-200">
                We retain {coverage.retainedVoyageCount.toLocaleString()} voyage records. Not all vessels are expected
                to be cleaning, so these numbers show what is available—not a cleaning compliance score.
              </p>
              {coverage.sources ? (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <AvailabilityTile
                    label="Voyages with records"
                    metric={coverage.sources.anyProcessed}
                    description="A form, relevant email report, insight, note, manual entry or photo"
                  />
                  <AvailabilityTile
                    label="Cleaning form"
                    metric={coverage.sources.cleaningForms}
                    description="A readable Veslink form"
                  />
                  <AvailabilityTile
                    label="Email insight"
                    metric={coverage.sources.extractedEmails}
                    description="Cleaning details extracted from an email"
                  />
                  <AvailabilityTile
                    label="Photo record"
                    metric={coverage.sources.images}
                    description="A linked image or manual upload"
                  />
                </div>
              ) : (
                <p className="mt-3 text-sm opacity-70">The source breakdown is not available yet.</p>
              )}
              <p className="mt-3 text-xs opacity-70">
                A voyage can appear in more than one group; these counts are not additive.
              </p>
              <details className="mt-4 border-t pt-4 text-xs">
                <summary className="cursor-pointer font-semibold text-[#176B62]">See sources and definitions</summary>
                <p className="mt-3 opacity-70">
                  One voyage can appear in several groups. An email linked by Shipfix may not have a retrievable Outlook
                  body. Photo counts indicate stored records, not verified S3 delivery or a known hold number. Snowflake
                  voyage facts and live Crew Report data are tracked separately.
                </p>
                {coverage.sources && (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full min-w-[560px] text-left text-xs">
                      <thead className="border-b opacity-70">
                        <tr>
                          <th className="py-2 font-semibold">Source or record</th>
                          <th className="py-2 text-right font-semibold">Voyages</th>
                          <th className="px-3 py-2 font-semibold">What it means</th>
                        </tr>
                      </thead>
                      <tbody>
                        <SourceRow
                          label="Any linked data"
                          metric={coverage.sources.anyLinked}
                          meaning="Includes email candidates that may be unavailable"
                        />
                        <SourceRow
                          label="Processed cleaning record"
                          metric={coverage.sources.anyProcessed}
                          meaning="Form, relevant email report, extracted email, manual record, note or photo"
                        />
                        <SourceRow
                          label="Veslink cleaning form"
                          metric={coverage.sources.cleaningForms}
                          meaning="Parsed cleaning form from Snowflake"
                        />
                        <SourceRow
                          label="Veslink inventory form"
                          metric={coverage.sources.inventoryForms}
                          meaning="Parsed inventory form from Snowflake"
                        />
                        <SourceRow
                          label="Linked Shipfix email"
                          metric={coverage.sources.emailMessages}
                          meaning="Matched email candidate; body may be unavailable"
                        />
                        <SourceRow
                          label="Extracted email"
                          metric={coverage.sources.extractedEmails}
                          meaning="Relevant content extracted successfully"
                        />
                        <SourceRow
                          label="Manual entry"
                          metric={coverage.sources.manualEntries}
                          meaning="User-entered structured cleaning record"
                        />
                        <SourceRow
                          label="Photo record"
                          metric={coverage.sources.images}
                          meaning="Validated image record or manual upload"
                        />
                      </tbody>
                    </table>
                  </div>
                )}
                <h3 className="mt-5 font-semibold">What the extracted emails mention</h3>
                <p className="mt-1 opacity-70">
                  These counts use {coverage.emailLinkedVoyageCount.toLocaleString()} voyages with linked emails. Forms
                  and manual entries are excluded. Silence does not prove that an issue did not occur.
                </p>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full min-w-[620px] text-left text-xs">
                    <thead className="border-b opacity-70">
                      <tr>
                        <th className="px-2 py-2 font-semibold">Information found in email</th>
                        <th className="px-2 py-2 text-right font-semibold">Voyages with a mention</th>
                        <th className="px-2 py-2 text-right font-semibold">Share of email-linked voyages</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverage.fields.map((field) => (
                        <tr key={field.key} className="border-b last:border-b-0">
                          <td className="px-2 py-2">
                            <span className="font-medium">{COVERAGE_FIELD_LABELS[field.key].label}</span>
                            <span className="block opacity-70">{COVERAGE_FIELD_LABELS[field.key].meaning}</span>
                          </td>
                          <td className="px-2 py-2 text-right">
                            {field.coveredCount.toLocaleString()} of {field.totalCount.toLocaleString()}
                          </td>
                          <td className="px-2 py-2 text-right">{field.percentage.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}
          <details className="mt-5 border-t pt-4" data-test="hold-cleaning-email-diagnostics">
            <summary className="cursor-pointer text-xs font-semibold uppercase">Email processing diagnostics</summary>
            <p className="mt-2 text-xs opacity-70">
              {emailStatus?.modelName ?? "openai/gpt-5.4-nano"} · classifier{" "}
              {emailStatus?.classifierContractVersion ?? "-"} · extractor {emailStatus?.extractorContractVersion ?? "-"}
            </p>
            <p className="mt-2 text-xs opacity-70">
              {emailStatus?.scope
                ? "These counts and failures cover email candidates linked to retained voyages in the active dataset. "
                : "These rates use all stored Shipfix email candidates; historical records may be included. "}
              The latest revision counts as past Outlook only when its status proves it reached attachment processing,
              normalization or AI; old failures without stage evidence remain unverified. Relevant extraction uses
              relevant classified emails as its denominator.
            </p>
            {emailStatus?.scope && (
              <p className="mt-2 text-xs opacity-70">
                {emailStatus.scope.excludedEmailCount.toLocaleString()} historical email candidates outside this scope
                are excluded ({emailStatus.scope.storedEmailCount.toLocaleString()} stored overall). Cumulative AI usage
                still includes all tracked calls, including historical voyages.
              </p>
            )}
            {coverage && (
              <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                {coverage.sources && (
                  <CoverageMetric
                    label="Latest revision past Outlook"
                    metric={coverage.processing.outlookResolvable}
                    description="Reached attachment processing, normalization or AI; unknown legacy failures excluded"
                  />
                )}
                <CoverageMetric
                  label="AI classified"
                  metric={coverage.processing.classification}
                  description="Successfully marked relevant or irrelevant"
                />
                <CoverageMetric
                  label="Usable email outcomes"
                  metric={coverage.processing.usableOutcome}
                  description="Irrelevant classification or successful extraction"
                />
                <CoverageMetric
                  label="Relevant emails extracted"
                  metric={coverage.processing.relevantExtraction}
                  description="Extracted out of successfully classified relevant emails"
                />
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5 xl:grid-cols-10">
              <Metric
                label={emailStatus?.scope ? "Email candidates in scope" : "Stored email candidates"}
                value={emailStatus?.discoveredCount ?? 0}
              />
              <Metric label="Attempted" value={emailStatus?.attemptedCount ?? emailStatus?.processedCount ?? 0} />
              <Metric label="Classified" value={emailStatus?.processedCount ?? 0} />
              <Metric label="Relevant" value={emailStatus?.relevantCount ?? 0} />
              <Metric label="Irrelevant" value={emailStatus?.irrelevantCount ?? 0} />
              <Metric label="Extracted" value={emailStatus?.extractedCount ?? 0} />
              <Metric label="Remaining" value={emailStatus?.remainingCount ?? 0} tone="warning" />
              <Metric
                label="Retryable failures"
                value={emailStatus?.retryableFailureCount ?? 0}
                tone={emailStatus?.retryableFailureCount ? "danger" : "success"}
              />
              <Metric
                label="Final failures"
                value={emailStatus?.finalFailureCount ?? 0}
                tone={emailStatus?.finalFailureCount ? "danger" : "success"}
              />
              <Metric
                label="Source unavailable"
                value={emailStatus?.sourceUnavailableCount ?? 0}
                tone={emailStatus?.sourceUnavailableCount ? "warning" : "success"}
              />
            </div>
            {emailStatus?.latestRunUsage && <HoldCleaningRunCost usage={emailStatus.latestRunUsage} />}
            {emailStatus?.cumulativeUsage ? (
              <div className="mt-4">
                <h3 className="text-xs font-semibold uppercase">Cumulative worker AI usage</h3>
                <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-3">
                  <Metric
                    label="Estimated cost (reported usage)"
                    value={`$${emailStatus.cumulativeUsage.estimatedCostUsd.toFixed(4)}`}
                  />
                  <Metric label="Tracked calls" value={emailStatus.cumulativeUsage.invocationCount} />
                  <Metric
                    label="Calls with unreported usage"
                    value={emailStatus.cumulativeUsage.unreportedInvocationCount}
                    tone={emailStatus.cumulativeUsage.unreportedInvocationCount ? "warning" : "success"}
                  />
                </div>
                <p className="mt-3 text-xs opacity-70">
                  {emailStatus.cumulativeUsage.trackingSince
                    ? `Tracking since ${formatHoldCleaningDate(emailStatus.cumulativeUsage.trackingSince)}. Full refreshes do not reset this total.`
                    : "Tracking starts with the next worker AI call."}{" "}
                  Earlier calls are excluded. Unreported usage includes interrupted or in-flight calls; its cost is
                  unknown. This is an estimate, not an invoice.
                </p>
                <p className="mt-1 text-xs opacity-70">
                  Reported tokens: {emailStatus.cumulativeUsage.promptTokens.toLocaleString()} input |{" "}
                  {emailStatus.cumulativeUsage.completionTokens.toLocaleString()} output |{" "}
                  {emailStatus.cumulativeUsage.totalTokens.toLocaleString()} total. Pricing:{" "}
                  {emailStatus.cumulativeUsage.pricingVersions.join(", ") || "No calls yet"}.
                </p>
              </div>
            ) : (
              <p className="mt-3 text-xs opacity-70">
                Latest-revision estimate (not cumulative): ${(emailStatus?.estimatedCostUsd ?? 0).toFixed(4)}. Tokens:{" "}
                {(emailStatus?.promptTokens ?? 0).toLocaleString()} input |{" "}
                {(emailStatus?.completionTokens ?? 0).toLocaleString()} output. Historical calls are not fully tracked.
              </p>
            )}
            {emailStatus?.imageClassification && (
              <section
                className="border-current/10 mt-4 rounded-lg border p-3"
                aria-label="Image categorization progress"
              >
                <h3 className="text-sm font-semibold">Image categorization — preview</h3>
                <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                  <Metric label="Images categorized" value={emailStatus.imageClassification.classifiedCount} />
                  <Metric label="Awaiting category" value={emailStatus.imageClassification.remainingCount} />
                  <Metric label="Category failures" value={emailStatus.imageClassification.failedCount} />
                  <Metric label="Suggested hold photos" value={emailStatus.imageClassification.holdPhotoCount} />
                </div>
                <p className="mt-2 text-xs opacity-70">
                  Unique images in the retained voyage history, not voyages or lifetime calls. Up to{" "}
                  {emailStatus.imageClassification.batchLimit.toLocaleString()} images per worker run
                  {emailStatus.imageClassification.budgetMinutes
                    ? ` using the remaining worker time (up to ${emailStatus.imageClassification.budgetMinutes} minutes for the whole run)`
                    : ""}
                  ; {emailStatus.imageClassification.readyCount} ready now. Waiting includes deferred retries.
                  Categorization does not hide images or verify their capture date.
                </p>
                {emailStatus.imageClassification.totalCount > emailStatus.imageClassification.eligibleCount && (
                  <p className="mt-1 text-xs opacity-70">
                    {emailStatus.imageClassification.totalCount - emailStatus.imageClassification.eligibleCount} images
                    are outside the classifier's size or format limits; they are not counted as waiting.
                  </p>
                )}
              </section>
            )}
            {emailStatus?.imageCategoryUsage && (
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer font-semibold">Image categorization — preview usage</summary>
                <p className="mt-2">Included in the worker total above; not an additional charge.</p>
                <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-3">
                  <Metric label="Category calls" value={emailStatus.imageCategoryUsage.invocationCount} />
                  <Metric label="Reported tokens" value={emailStatus.imageCategoryUsage.totalTokens.toLocaleString()} />
                  <Metric
                    label="Estimated category cost"
                    value={`$${emailStatus.imageCategoryUsage.estimatedCostUsd.toFixed(4)}`}
                  />
                </div>
                <p className="mt-2">
                  {emailStatus.imageCategoryUsage.unreportedInvocationCount} calls have unreported usage. Suggestions do
                  not change Photos coverage. Run checkpoints show processed categories, deferred work and duration.
                </p>
              </details>
            )}
            <p className="mt-1 text-xs opacity-70">
              Latest processing {formatHoldCleaningDate(emailStatus?.latestRunAt)} | latest backfill{" "}
              {formatHoldCleaningDate(emailStatus?.latestBackfillAt)}.
            </p>
            <div className="mt-5">
              <h3 className="text-xs font-semibold uppercase">Processing outcome breakdown</h3>
              {failureBreakdown.length ? (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left text-xs">
                    <thead className="border-b opacity-70">
                      <tr>
                        <th className="px-2 py-2 font-semibold">Stage</th>
                        <th className="px-2 py-2 font-semibold">Status</th>
                        <th className="px-2 py-2 font-semibold">Outcome</th>
                        <th className="px-2 py-2 text-right font-semibold">Emails</th>
                        <th className="px-2 py-2 font-semibold">Latest occurrence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failureBreakdown.map((failure) => (
                        <tr
                          key={`${failure.stage}-${failure.status}-${failure.errorCode}`}
                          className="border-b last:border-b-0"
                        >
                          <td className="px-2 py-2">{failure.stage.replaceAll("_", " ").toLowerCase()}</td>
                          <td className="px-2 py-2">{failure.status.replaceAll("_", " ").toLowerCase()}</td>
                          <td className="px-2 py-2" title={failure.errorCode}>
                            {HOLD_CLEANING_EMAIL_OUTCOME_LABELS[failure.errorCode] ?? failure.errorCode}
                          </td>
                          <td className="px-2 py-2 text-right font-semibold">{failure.count.toLocaleString()}</td>
                          <td className="px-2 py-2">{formatHoldCleaningDate(failure.latestOccurredAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-2 text-xs opacity-70">
                  No current email-intelligence failures or unavailable sources.
                </p>
              )}
            </div>
          </details>
        </section>
        <section className={twMerge(adminStyles.card.surface, "p-5")}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">Source health</h2>
            {canOpenCrewReport && (
              <Link className="text-xs font-semibold text-green underline" to={ADMIN_PATHS.crewReport}>
                Open Crew Report administration
              </Link>
            )}
          </div>
          <p className="mt-2 text-xs opacity-70">
            Crew data is read live from the current OpsLink Crew Report and is monitored there.
          </p>
          <div className="mt-4 space-y-2">
            {health.map((source, index) => (
              <div
                key={`${source.source}-${index}`}
                className="flex items-center justify-between gap-3 border-t py-3 first:border-t-0"
              >
                <div>
                  <p className="text-sm font-semibold">{formatHoldCleaningSource(source.source)}</p>
                  <p className="mt-1 text-xs opacity-70">
                    Last success {formatHoldCleaningDate(source.lastSuccessfulAt)}
                  </p>
                  {source.warning && <p className="mt-1 text-xs text-[#946200]">{source.warning}</p>}
                </div>
                <HoldCleaningSourceStateBadge state={source.state} />
              </div>
            ))}
          </div>
        </section>

        <section className={twMerge(adminStyles.card.surface, "p-5")}>
          <h2 className="text-sm font-semibold">Recent runs</h2>
          <div className="mt-4 space-y-2">
            {runs.map((run) => (
              <button
                key={run.runId}
                type="button"
                className="w-full border-t py-3 text-left first:border-t-0"
                onClick={() => void inspectRun(run.runId)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-xs">{run.runId}</p>
                    <p className="mt-1 text-xs opacity-70">
                      {run.trigger} · {duration(run)} · {formatHoldCleaningDate(run.startedAt)}
                    </p>
                    {(run.completedAt || typeof run.validationSummary?.publishedAt === "string") && (
                      <p className="mt-1 text-xs opacity-70">
                        {typeof run.validationSummary?.publishedAt === "string"
                          ? "Duration includes post-publication image categorization."
                          : "Legacy recorded duration; may exclude post-publication work."}
                      </p>
                    )}
                    <HoldCleaningRunLifecycle run={run} />
                    {run.errorSummary && <p className="mt-1 text-xs text-[#BF0002]">{run.errorSummary}</p>}
                  </div>
                  <HoldCleaningStatusBadge value={run.status} />
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>

      {selectedRun && (
        <section className={twMerge(adminStyles.card.surface, "mt-5 p-5")}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold">Run stages and checkpoints</h2>
              <p className="mt-1 break-all font-mono text-xs">{selectedRun.runId}</p>
            </div>
            <div className="flex items-center gap-3">
              <HoldCleaningStatusBadge value={selectedRun.status} />
              {(selectedRun.status.includes("FAILED") || selectedRun.status === "STALE") && (
                <Link
                  className="text-xs font-semibold text-green underline"
                  to={`${ADMIN_PATHS.debugCronJobs}?job=hold-cleaning-refresh&retryRunId=${selectedRun.runId}`}
                >
                  Retry in Debug
                </Link>
              )}
            </div>
          </div>
          <HoldCleaningRunLifecycle run={selectedRun} />
          <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {selectedRun.sourceStatuses?.map((source) => (
              <div key={source.source} className="bg-[#F7FBFA] p-3 text-xs dark:bg-gray-900">
                <p className="font-semibold">{formatHoldCleaningSource(source.source)}</p>
                <p className="mt-1">{source.state}</p>
                <p className="mt-1 opacity-70">Checkpoint {formatHoldCleaningDate(source.checkpointAt)}</p>
                {source.warning && <p className="mt-1 text-[#BF0002]">{source.warning}</p>}
              </div>
            ))}
          </div>
          <details className="mt-4 text-xs">
            <summary className="cursor-pointer font-semibold">Validation and stage summary</summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap bg-[#F7FBFA] p-3 dark:bg-gray-900">
              {JSON.stringify(selectedRun.validationSummary ?? {}, null, 2)}
            </pre>
          </details>
        </section>
      )}
    </main>
  );
};

const Metric = ({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string | number;
  tone?: keyof typeof metricTones;
}) => (
  <div className={twMerge(adminStyles.card.indicatorCompact, metricTones[tone])}>
    <p className="text-[10px] font-semibold uppercase opacity-75">{label}</p>
    <p className="mt-2 break-words text-lg font-bold">{value}</p>
  </div>
);

const CoverageMetric = ({
  label,
  metric,
  description,
}: {
  label: string;
  metric: HoldCleaningCoverageMetric;
  description: string;
}) => (
  <div className={twMerge(adminStyles.card.indicatorCompact, metricTones.neutral)}>
    <p className="text-[10px] font-semibold uppercase opacity-75">{label}</p>
    <p className="mt-2 text-lg font-bold">{metric.percentage.toFixed(1)}%</p>
    <p className="mt-1 text-[11px] opacity-75">
      {metric.coveredCount.toLocaleString()} of {metric.totalCount.toLocaleString()}
    </p>
    <p className="mt-2 text-[11px] opacity-75">{description}</p>
  </div>
);

const AvailabilityTile = ({
  label,
  metric,
  description,
}: {
  label: string;
  metric: HoldCleaningCoverageMetric;
  description: string;
}) => (
  <div className="rounded-lg border border-[#DFE4E2] bg-white p-4 dark:bg-gray-800">
    <p className="text-xs font-semibold text-[#4A5655] dark:text-gray-300">{label}</p>
    <p className="mt-2 text-2xl font-bold text-[#176B62] dark:text-[#8DD7CE]">{metric.coveredCount.toLocaleString()}</p>
    <p className="mt-1 text-xs text-[#4A5655] dark:text-gray-300">{description}</p>
  </div>
);

const SourceRow = ({
  label,
  metric,
  meaning,
}: {
  label: string;
  metric: HoldCleaningCoverageMetric;
  meaning: string;
}) => (
  <tr className="border-b last:border-b-0">
    <td className="py-2 font-medium">{label}</td>
    <td className="py-2 text-right font-semibold">{metric.coveredCount.toLocaleString()}</td>
    <td className="px-3 py-2 opacity-70">{meaning}</td>
  </tr>
);

export default HoldCleaningMonitoringPanel;
